// ============================================================================
// QUERY KEYS CONSTANTS - House of Kairos OS
// ============================================================================
// 
// This file centralizes all React Query key patterns to ensure consistency
// across the codebase and prevent typos or inconsistencies.
// 
// Naming Convention:
// - Use kebab-case for multi-word keys
// - Use singular form for individual items
// - Use plural form for lists
// - Include entity type in the key name
// - Use descriptive suffixes for specialized queries

// ============================================================================
// CORE ENTITY QUERY KEYS
// ============================================================================

/**
 * Member-related query keys
 */
export const MEMBER_QUERY_KEYS = {
  // List queries
  ALL: 'members',
  DELETED: 'deleted-members',

  // Individual queries
  BY_ID: (id: string) => ['member', id] as const,

  // Specialized queries
  PRIVACY_SETTINGS: (memberId: string) => ['member-privacy-settings', memberId] as const,
  DELETION_IMPACT: (memberId: string) => ['member-deletion-impact', memberId] as const,
  CHECK_INS: (memberId: string) => ['member-check-ins', memberId] as const,
  NOTES: (memberId: string) => ['member-notes', memberId] as const,
  ACTIVITY_LOGS: (memberId: string) => ['member-activity-logs', memberId] as const,
  REPORT: 'members-report',
  AUTH_DETAILS_PREFIX: 'member-auth-details',
  AUTH_INFO_PREFIX: 'member-auth-info',
  DETAILS_FOR_LOGS_PREFIX: 'member-details-for-logs',
  FILES_PREFIX: 'member-files',
  LIFETIME_SPEND_PREFIX: 'member-lifetime-spend',
  WAIVER_THANK_YOU_PREFIX: 'member-waiver-thank-you',
} as const;

/**
 * Membership-related query keys
 */
export const MEMBERSHIP_QUERY_KEYS = {
  // List queries
  ALL: 'memberships',

  // Individual queries
  BY_ID: (id: string) => ['membership', id] as const,
  RECEIPTS: (id: string) => ['membership-receipts', id] as const,

  // Member-specific queries
  BY_MEMBER: (memberId: string) => ['member-memberships', memberId] as const,
  BULK_BY_MEMBERS: (memberIds: string[]) => ['bulk-member-memberships', memberIds] as const,

  // Membership-detail-screen queries (key prefixes for prefix-match invalidation)
  FREEZES_PREFIX: 'membership-freezes',
  FREEZE_SUMMARY_PREFIX: 'membership-freeze-summary',
  ACTIVITY_LOGS_PREFIX: 'membership-activity-logs',
  TRANSFERS: (membershipId: string) => ['membership-transfers', membershipId] as const,
  TRANSFER_FEE_REPORT: 'membership-transfer-fee-report',
  REFUNDS: (membershipId: string) => ['membership-refunds', membershipId] as const,
  REFUNDS_PREFIX: 'membership-refunds',
  REFUNDS_UNSETTLED: 'membership-refunds-unsettled',
  REFUND_CONTEXT: (membershipId: string) => ['membership-refund-context', membershipId] as const,
  REFUND_CONTEXT_PREFIX: 'membership-refund-context',

  // Reports
  ACTIVE_MEMBER_GROWTH: 'active-member-growth',
  RENEWALS_REPORT: 'renewals-report',
  BULK_BY_MEMBERS_PREFIX: 'bulk-member-memberships',
  BY_MEMBER_PREFIX: 'member-memberships',
  MEMBER_DETAILS_FOR_LOGS_PREFIX: 'member-details-for-membership-logs',
  NOTES_PREFIX: 'membership-notes',
  TRANSFER_DESTINATION_CHECK_PREFIX: 'transfer-destination-active-check',
} as const;

/**
 * Membership Type-related query keys
 */
export const MEMBERSHIP_TYPE_QUERY_KEYS = {
  // List queries
  ALL: 'membership-types',
  
  // Individual queries
  BY_ID: (id: string) => ['membership-type', id] as const,
} as const;

/**
 * Class-related query keys
 */
export const CLASS_QUERY_KEYS = {
  // List queries
  ALL: 'classes',
  BY_COACH: (coachId: string) => ['classes', 'coach', coachId] as const,
  
  // Individual queries
  BY_ID: (id: string) => ['class', id] as const,
  ACTIVITY_LOGS_PREFIX: 'class-activity-logs',
} as const;

/**
 * Class Category-related query keys
 */
export const CLASS_CATEGORY_QUERY_KEYS = {
  // List queries
  ALL: 'class-categories',
  
  // Individual queries
  BY_ID: (id: string) => ['class-category', id] as const,
  FOR_CHANGELOG: 'categories-for-changelog',
} as const;

/**
 * Class Booking-related query keys
 */
export const CLASS_BOOKING_QUERY_KEYS = {
  // Class-specific queries
  BY_CLASS: (classId: string) => ['class-bookings', classId] as const,
  
  // Member-specific queries
  BY_MEMBER: (memberId: string) => ['member-class-bookings', memberId] as const,
  BY_CLASS_AND_MEMBER: (classId: string, memberId: string) => 
    ['member-class-booking', classId, memberId] as const,
} as const;

/**
 * Bookable resource (call booths, future recovery room) query keys
 */
export const BOOKABLE_RESOURCE_QUERY_KEYS = {
  // List queries
  ALL: 'bookable-resources',

  // Individual queries
  BY_ID: (id: string) => ['bookable-resource', id] as const,
  PUBLIC_AVAILABILITY_PREFIX: 'public-booth-availability',
} as const;

/**
 * Resource booking-related query keys
 */
export const RESOURCE_BOOKING_QUERY_KEYS = {
  // All bookings for a set of resources on a Bali calendar day. The ids are sorted
  // into one stable segment so key identity doesn't depend on array order.
  BY_DAY: (resourceIds: string[], baliDateKey: string) =>
    ['resource-bookings', baliDateKey, [...resourceIds].sort().join(',')] as const,

  // Member-specific queries
  BY_MEMBER: (memberId: string) => ['member-resource-bookings', memberId] as const,
  BY_DAY_PREFIX: 'resource-bookings',
} as const;

/**
 * Locker / locker key assignment query keys
 */
export const LOCKER_QUERY_KEYS = {
  // Static locker config (124 rows, seeded by migration)
  ALL: 'lockers',

  // Open assignments only (returned_at IS NULL) with member + locker joins
  ACTIVE_ASSIGNMENTS: 'locker-active-assignments',

  // get_locker_usage_stats() RPC (one row per locker)
  USAGE_STATS: 'locker-usage-stats',

  // Paginated assignment history. HISTORY_PREFIX invalidates every page/filter.
  HISTORY_PREFIX: 'locker-history',
  HISTORY: (page: number, room: string) => ['locker-history', room, page] as const,

  // Member-specific queries
  BY_MEMBER: (memberId: string) => ['member-locker-assignments', memberId] as const,
} as const;

/**
 * Personal Training session-related query keys
 */
export const PT_SESSION_QUERY_KEYS = {
  ALL: 'pt-sessions',
  LEDGER: 'pt-sessions-ledger',
  BY_ID: (id: string) => ['pt-session', id] as const,
  BY_MEMBER: (memberId: string) => ['pt-sessions-by-member', memberId] as const,
  BY_COACH: (coachId: string) => ['pt-sessions-by-coach', coachId] as const,
  ACTIVE_COUNT_PREFIX: 'pt-sessions-active-count',
} as const;

/**
 * Gift card-related query keys
 */
export const GIFT_CARD_QUERY_KEYS = {
  ALL: 'gift-cards',
  BY_ID: (id: string) => ['gift-card', id] as const,
  BY_CODE: (code: string) => ['gift-card-by-code', code] as const,
  REDEMPTIONS: (id: string) => ['gift-card-redemptions', id] as const,
} as const;

/**
 * PT session pack-related query keys
 */
export const PT_SESSION_PACK_QUERY_KEYS = {
  ALL: 'pt-session-packs',
  BY_ID: (id: string) => ['pt-session-pack', id] as const,
  BY_MEMBER: (memberId: string) => ['pt-session-packs-by-member', memberId] as const,
} as const;

/**
 * PT session pack type (template) query keys
 */
export const PT_SESSION_PACK_TYPE_QUERY_KEYS = {
  ALL: 'pt-session-pack-types',
  BY_ID: (id: string) => ['pt-session-pack-type', id] as const,
} as const;

/**
 * Waitlist-related query keys
 */
export const WAITLIST_QUERY_KEYS = {
  // Class-specific queries
  BY_CLASS: (classId: string) => ['class-waitlist', classId] as const,
  // No COUNT_BY_CLASS: waitlist_count is derived by the classes list query
  // (CLASS_QUERY_KEYS.ALL), so there is nothing registered under a count key of
  // its own. One existed here and was invalidated from six call sites, none of
  // which did anything — and because those calls looked like the count was
  // being refreshed, the badge going stale read as a caching quirk rather than
  // a missing invalidation. Invalidate CLASS_QUERY_KEYS.ALL instead.
  BY_CLASS_PREFIX: 'class-waitlist',
  MEMBER_CHECK_PREFIX: 'member-waitlist-check',
} as const;

/**
 * Check-in-related query keys
 */
export const CHECK_IN_QUERY_KEYS = {
  // List queries
  ALL: 'check-ins',
  
  // Time-based queries
  IN_THE_HOUSE: 'in-the-house',
  LAST_30_DAYS: 'check-ins-last-30-days',
  TODAY: 'check-ins-today',
  
  // Dashboard queries
  DATA: 'check-ins-data',
} as const;

/**
 * Admin User-related query keys
 */
export const ADMIN_USER_QUERY_KEYS = {
  // List queries
  ALL: 'admin-users',

  // Individual queries.
  // id is optional because useAdminUser is called with an undefined route param
  // and gates the fetch on `enabled: Boolean(id)` — the key is still built. The
  // key that produces, ['admin-user', undefined], is what the inline literal
  // here always built; widening the type just stops it being a silent one.
  BY_ID: (id: string | undefined) => ['admin-user', id] as const,
  CURRENT: 'current-admin-user',

  // Permission queries
  CURRENT_PERMISSIONS: 'current-user-permissions',
  BY_ID_PREFIX: 'admin-user',
  VAULTED_AVATARS_PREFIX: 'vaulted-admin-avatars',
} as const;

/**
 * Activity Log-related query keys
 */
export const ACTIVITY_LOG_QUERY_KEYS = {
  // List queries with filters
  ALL: (options?: Record<string, unknown>) => ['activity-logs', options] as const,
  PREFIX: 'activity-logs',
} as const;

// ============================================================================
// PHOTO-RELATED QUERY KEYS
// ============================================================================

/**
 * Member Photo-related query keys
 */
export const MEMBER_PHOTO_QUERY_KEYS = {
  // Webcam photos
  WEBCAM_BY_MEMBER: (memberId: string) => ['member-webcam-photos', memberId] as const,
  ALL_WEBCAM_BY_MEMBER: (memberId: string) => ['all-member-webcam-photos', memberId] as const,
  BULK_WEBCAM: (memberIds: string[]) => ['bulk-member-webcam-photos', memberIds] as const,
  
  // Profile photos (future feature)
  PROFILE_BY_MEMBER: (memberId: string) => ['member-profile-photos', memberId] as const,
  ALL_PROFILE_BY_MEMBER: (memberId: string) => ['all-member-profile-photos', memberId] as const,
  BULK_PROFILE: (memberIds: string[]) => ['bulk-member-profile-photos', memberIds] as const,
  ALL_WEBCAM_BY_MEMBER_PREFIX: 'all-member-webcam-photos',
  BULK_WEBCAM_PREFIX: 'bulk-member-webcam-photos',
  BY_MEMBER_PREFIX: 'member-photos',
  UPLOADED_BY_MEMBER_PREFIX: 'member-uploaded-photos',
  WEBCAM_BY_MEMBER_PREFIX: 'member-webcam-photos',
} as const;

// ============================================================================
// DASHBOARD & STATISTICS QUERY KEYS
// ============================================================================

/**
 * Dashboard-related query keys
 */
export const DASHBOARD_QUERY_KEYS = {
  // Count queries
  MEMBERS_COUNT: 'members-count',
  CLASSES_COUNT: 'classes-count',
  TODAY_CLASSES_COUNT: 'today-classes-count',
  ACTIVE_MEMBERSHIPS_COUNT: 'active-memberships-count',
  TODAY_CHECK_INS_COUNT: 'today-check-ins-count',
  ACTIVE_MEMBERSHIPS_VALUE: 'active-memberships-value',
  IN_THE_HOUSE: 'in-the-house',
  
  // Chart queries
  ACTIVE_MEMBERSHIPS_CHART: 'active-memberships-chart',
  LATEST_CHECK_INS: 'latest-check-ins',
  LATEST_MEMBERSHIPS: 'latest-memberships',
  TODAY_FNB_REVENUE: 'today-fnb-revenue',
  YESTERDAY_FNB_REVENUE: 'yesterday-fnb-revenue',
  YESTERDAY_MEMBERSHIP_REVENUE: 'yesterday-membership-revenue',
} as const;

/**
 * Finance-related query keys
 */
export const FINANCE_QUERY_KEYS = {
  // Calculations
  REFUND_SUMMARY: 'finance-refund-summary',
  NAMESPACE: 'finance',
} as const;

// ============================================================================
// STORAGE-RELATED QUERY KEYS
// ============================================================================

/**
 * Storage-related query keys
 */
export const STORAGE_QUERY_KEYS = {
  // Statistics
  STATS: 'storage-stats',
  DETAILED_STATS: 'storage-detailed-stats',
  BY_MEMBER_PREFIX: 'member-storage-stats',
} as const;

/**
 * Inventory-related query keys (Odoo apparel/merch stock snapshot).
 */
/**
 * Employee-related query keys.
 *
 * Moved here from a module-private `QUERY_KEYS` in useEmployees.ts. A local
 * factory is invisible to the reconciliation test, which compares the keys
 * queries register against the keys mutations invalidate: the purchases key was
 * registered by that private object and invalidated from EmployeeOdooSection,
 * and the check could only see the invalidation, so it read as dead. Keys have
 * to live in one place for that comparison to mean anything.
 */
export const EMPLOYEE_QUERY_KEYS = {
  // List queries
  ALL: 'employees',

  // Individual queries
  BY_ID: (id: string) => ['employee', id] as const,

  // Odoo POS ledger for one employee (super_admin-scoped)
  PURCHASES: (id: string) => ['employee-odoo-purchases', id] as const,
} as const;

/**
 * Inventory-related query keys
 */
export const INVENTORY_QUERY_KEYS = {
  ALL: ['inventory'] as const,
} as const;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Helper function to create query keys with proper typing
 */
export const createQueryKey = <T extends readonly unknown[]>(...args: T): T => args;

/**
 * Helper function to invalidate multiple related queries
 */
export const invalidateRelatedQueries = (
  queryClient: { invalidateQueries: (options: { queryKey: string | readonly unknown[] }) => void },
  queryKeys: (string | readonly unknown[])[]
) => {
  queryKeys.forEach(queryKey => {
    queryClient.invalidateQueries({ queryKey });
  });
};

// ============================================================================
// TYPE EXPORTS
// ============================================================================

/**
 * Type for all possible query keys
 */
/**
 * Report-screen query keys. NAMESPACE is the shared root of the
 * `['reports', <report>, ...filters]` family, which varies in arity — a string
 * root is the only form that can express every member of it.
 */
export const REPORT_QUERY_KEYS = {
  AGE: 'age-report',
  CLASSES: 'classes-report',
  CHECK_INS_BY_HOUR: 'reports-check-ins-by-hour',
  CHECK_INS_DAILY_COUNTS: 'reports-check-ins-daily-counts',
  CHECK_INS_LAST_30_DAYS: 'reports-check-ins-last-30-days',
  CHECK_INS_TODAY: 'reports-check-ins-today',
  FREEZE: 'freeze-report',
  GENDER: 'gender-report',
  IN_THE_HOUSE: 'reports-in-the-house',
  MEMBERSHIPS: 'memberships-report',
  MEMBERSHIP_COHORTS: 'membership-cohorts-report',
  MEMBERSHIP_SPLIT: 'membership-split-report',
  MEMBER_CREATION: 'member-creation-report',
  MEMBER_TYPES: 'member-types-report',
  MRR: 'mrr-report',
  NAMESPACE: 'reports',
  NATIONALITY: 'nationality-report',
  PT_SESSIONS: 'pt-sessions-report',
  REFUNDS: 'refunds-report',
  TOP_CHECK_INS_BY_MEMBERS: 'reports-top-check-ins-by-members',
  TOTAL_CHECK_INS: 'reports-total-check-ins',
} as const;

/**
 * Coach-related query keys.
 */
export const COACH_QUERY_KEYS = {
  ALL: 'coaches',
  BY_ID_PREFIX: 'coach',
  FOR_CHANGELOG: 'coaches-for-changelog',
  /** Suffix under ALL (`[ALL, WITH_STATS]`) so coach mutations' `[ALL]` invalidation covers it. */
  WITH_STATS: 'with-stats',
  /**
   * The member a coach is linked to (`[LINKED_MEMBER_PREFIX, memberId]`).
   * Its own root rather than a member key: it holds a partial member row, and
   * writing that into the member-detail cache would leave the member page
   * reading a row with most of its fields missing. Linking/unlinking changes
   * `coaches.member_id`, which changes this key, so it needs no invalidation.
   */
  LINKED_MEMBER_PREFIX: 'coach-linked-member',
} as const;

/**
 * Class-location query keys.
 */
export const CLASS_LOCATION_QUERY_KEYS = {
  ALL: 'class-locations',
  BY_ID_PREFIX: 'class-location',
} as const;

/**
 * BPA query keys.
 */
export const BPA_QUERY_KEYS = {
  ALL: 'bpa',
} as const;

/**
 * Odoo sync + POS ledger query keys.
 */
export const ODOO_QUERY_KEYS = {
  NAMESPACE: 'odoo',
  PURCHASES_PREFIX: 'odoo-purchases',
} as const;

export type QueryKey = 
  | typeof MEMBER_QUERY_KEYS[keyof typeof MEMBER_QUERY_KEYS]
  | typeof MEMBERSHIP_QUERY_KEYS[keyof typeof MEMBERSHIP_QUERY_KEYS]
  | typeof MEMBERSHIP_TYPE_QUERY_KEYS[keyof typeof MEMBERSHIP_TYPE_QUERY_KEYS]
  | typeof GIFT_CARD_QUERY_KEYS[keyof typeof GIFT_CARD_QUERY_KEYS]
  | typeof CLASS_QUERY_KEYS[keyof typeof CLASS_QUERY_KEYS]
  | typeof CLASS_CATEGORY_QUERY_KEYS[keyof typeof CLASS_CATEGORY_QUERY_KEYS]
  | typeof CLASS_BOOKING_QUERY_KEYS[keyof typeof CLASS_BOOKING_QUERY_KEYS]
  | typeof BOOKABLE_RESOURCE_QUERY_KEYS[keyof typeof BOOKABLE_RESOURCE_QUERY_KEYS]
  | typeof RESOURCE_BOOKING_QUERY_KEYS[keyof typeof RESOURCE_BOOKING_QUERY_KEYS]
  | typeof LOCKER_QUERY_KEYS[keyof typeof LOCKER_QUERY_KEYS]
  | typeof PT_SESSION_QUERY_KEYS[keyof typeof PT_SESSION_QUERY_KEYS]
  | typeof PT_SESSION_PACK_QUERY_KEYS[keyof typeof PT_SESSION_PACK_QUERY_KEYS]
  | typeof PT_SESSION_PACK_TYPE_QUERY_KEYS[keyof typeof PT_SESSION_PACK_TYPE_QUERY_KEYS]
  | typeof WAITLIST_QUERY_KEYS[keyof typeof WAITLIST_QUERY_KEYS]
  | typeof CHECK_IN_QUERY_KEYS[keyof typeof CHECK_IN_QUERY_KEYS]
  | typeof ADMIN_USER_QUERY_KEYS[keyof typeof ADMIN_USER_QUERY_KEYS]
  | typeof ACTIVITY_LOG_QUERY_KEYS[keyof typeof ACTIVITY_LOG_QUERY_KEYS]
  | typeof MEMBER_PHOTO_QUERY_KEYS[keyof typeof MEMBER_PHOTO_QUERY_KEYS]
  | typeof DASHBOARD_QUERY_KEYS[keyof typeof DASHBOARD_QUERY_KEYS]
  | typeof FINANCE_QUERY_KEYS[keyof typeof FINANCE_QUERY_KEYS]
  | typeof STORAGE_QUERY_KEYS[keyof typeof STORAGE_QUERY_KEYS]
  | typeof REPORT_QUERY_KEYS[keyof typeof REPORT_QUERY_KEYS]
  | typeof COACH_QUERY_KEYS[keyof typeof COACH_QUERY_KEYS]
  | typeof CLASS_LOCATION_QUERY_KEYS[keyof typeof CLASS_LOCATION_QUERY_KEYS]
  | typeof BPA_QUERY_KEYS[keyof typeof BPA_QUERY_KEYS]
  | typeof ODOO_QUERY_KEYS[keyof typeof ODOO_QUERY_KEYS]
  | typeof INVENTORY_QUERY_KEYS[keyof typeof INVENTORY_QUERY_KEYS];
